import { holdsAnywhere } from '@carii/casl-abilities';
import { useQuery } from '@tanstack/react-query';
import { Ban, Eye, Pencil, Tag, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { Select } from '../../components/ui/FormField.js';
import { ListSearch } from '../../components/ui/ListSearch.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { RowActions } from '../../components/ui/RowActions.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useConfirmedMutation } from '../../hooks/useConfirmedMutation.js';
import { useListControls } from '../../hooks/useListControls.js';
import { type Discount, type DiscountScope, discountsApi } from '../../services/discounts.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import {
  ApprovalBadge,
  ApprovalChips,
  type ApprovalFilterValue,
  PendingBanner,
  approvalFromUrl,
  requestLine,
} from '../approvals/approval.js';

type ScopeFilter = 'all' | DiscountScope;

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatValue(d: Discount) {
  return d.discount_type === 'percent'
    ? `${d.discount_value}%`
    : `RM ${(d.discount_value / 100).toFixed(2)}`;
}

function label(d: Discount) {
  return d.scope === 'code' ? (d.name ?? '—') : (d.customer?.label ?? '(deleted customer)');
}

export function DiscountListPage() {
  const ability = useAbility();
  // 372 (#1240): creating and editing are separate permissions (a CRM role creates; approving is apart). Admin holds
  // both through `manage`.
  const canCreate = holdsAnywhere(ability as never, 'create', 'Discount');
  const canManage = holdsAnywhere(ability as never, 'update', 'Discount');
  const canReview = holdsAnywhere(ability as never, 'approve', 'Discount');
  const [scope, setScope] = useState<ScopeFilter>('all');
  const { search: urlSearch } = useLocation();
  const [approvalFilter, setApprovalFilter] = useState<ApprovalFilterValue>(() =>
    approvalFromUrl(urlSearch),
  );
  // 162 (#21): this list already had a pager; what it lacked was a way to find one campaign.
  // `resetOn` carries the scope tabs, so switching them returns to page 1 (FR-007).
  const { page, setPage, searchInput, setSearchInput, search } = useListControls({
    resetOn: [scope],
  });

  const { data, isLoading, error } = useQuery({
    queryKey: [...RESOURCE_KEYS.discount.list, scope, page, search, approvalFilter],
    queryFn: () =>
      discountsApi.list({
        ...(scope === 'all' ? {} : { scope }),
        page,
        limit: 20,
        // Only when there is something to filter by (FR-004).
        q: search || undefined,
        approval: approvalFilter || undefined,
      }),
    // 063 (FR-010): a refusal is not a transient fault; retrying it only keeps
    // the caller reading the empty state.
    retry: false,
  });

  const remove = useConfirmedMutation({
    mutationFn: (id: string) => discountsApi.remove(id),
    invalidateKey: RESOURCE_KEYS.discount.list,
  });
  const deactivate = useConfirmedMutation({
    mutationFn: (id: string) => discountsApi.deactivate(id),
    invalidateKey: RESOURCE_KEYS.discount.list,
  });

  function rowActions(d: Discount) {
    // 372 (review): someone who may approve but not edit still needs a way to the voucher's Approval panel.
    if (!canManage)
      return (
        <RowActions
          actions={[
            canReview && {
              key: 'review',
              label: 'Review',
              href: `/discounts/${d.id}/edit`,
              icon: <Eye className="h-4 w-4" />,
            },
          ]}
        />
      );
    if (d.scope === 'code') {
      return (
        <RowActions
          actions={[
            {
              key: 'codes',
              label: 'Codes',
              href: `/discounts/${d.id}/codes`,
              icon: <Tag className="h-4 w-4" />,
            },
            {
              key: 'edit',
              label: 'Edit',
              href: `/discounts/${d.id}/edit`,
              icon: <Pencil className="h-4 w-4" />,
            },
            {
              key: 'delete',
              label: 'Delete',
              variant: 'danger',
              confirmMessage: `Delete discount "${label(d)}"? This cannot be undone.`,
              onClick: () => remove.run(d.id),
              icon: <Trash2 className="h-4 w-4" />,
            },
          ]}
        />
      );
    }
    return (
      <RowActions
        actions={[
          {
            key: 'edit',
            label: 'Edit',
            href: `/discounts/${d.id}/edit`,
            icon: <Pencil className="h-4 w-4" />,
          },
          d.is_active && {
            key: 'deactivate',
            label: 'Deactivate',
            variant: 'danger',
            confirmMessage: `Deactivate the discount for "${label(d)}"? The customer will no longer receive it.`,
            onClick: () => deactivate.run(d.id),
            icon: <Ban className="h-4 w-4" />,
          },
        ]}
      />
    );
  }

  const columns: Column<Discount>[] = [
    ...(scope === 'all'
      ? [
          {
            key: 'scope',
            header: 'Scope',
            render: (d: Discount) => (d.scope === 'code' ? 'Code' : 'Customer'),
          } as Column<Discount>,
        ]
      : []),
    { key: 'name', header: scope === 'customer' ? 'Customer' : 'Name / Customer', render: label },
    { key: 'discount_value', header: 'Discount', render: formatValue },
    ...(scope === 'code'
      ? [
          {
            key: 'valid_from',
            header: 'Valid',
            render: (d: Discount) => `${formatDate(d.valid_from)} → ${formatDate(d.valid_until)}`,
          } as Column<Discount>,
          {
            key: 'redemptions_count',
            header: 'Redeemed',
            render: (d: Discount) =>
              d.total_redemptions_limit
                ? `${d.redemptions_count ?? 0}/${d.total_redemptions_limit}`
                : `${d.redemptions_count ?? 0}`,
          } as Column<Discount>,
        ]
      : []),
    {
      key: 'approval',
      header: 'Approval',
      render: (d: Discount) => (
        <div>
          <ApprovalBadge approval={d.approval} />
          {d.approval?.status === 'pending' && (
            <div className="text-xs text-steel">{requestLine(d.approval, 'voucher')}</div>
          )}
        </div>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (d: Discount) => <StatusBadge status={d.is_active ? 'active' : 'inactive'} />,
    },
    { key: 'id', header: 'Actions', render: rowActions },
  ];

  const pages = data?.pages ?? (data ? Math.max(1, Math.ceil(data.total / 20)) : 1);

  return (
    <div>
      <PageHeader
        title="Discounts"
        description="Code campaigns and per-customer discounts, unified."
        actions={
          canCreate ? (
            <Link to="/discounts/new">
              <Button>+ New Discount</Button>
            </Link>
          ) : undefined
        }
      />
      <Card className="mb-4 flex gap-3">
        <Select
          id="discount_scope_filter"
          aria-label="Scope"
          data-testid="discount-scope-filter"
          value={scope}
          onChange={(e) => {
            setScope((e.target as HTMLSelectElement).value as ScopeFilter);
            setPage(1);
          }}
        >
          <option value="all">All scopes</option>
          <option value="code">Code</option>
          <option value="customer">Customer</option>
        </Select>
      </Card>
      <PendingBanner count={data?.pending_count ?? 0} kind="voucher" />
      <ApprovalChips
        value={approvalFilter}
        onChange={(v) => {
          setApprovalFilter(v);
          setPage(1);
        }}
        pendingCount={data?.pending_count}
      />
      <ListSearch
        value={searchInput}
        onChange={setSearchInput}
        label="Search campaigns by name"
        testId="discount-search"
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        keyExtractor={(r) => r.id}
        emptyMessage="No discounts yet"
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: pages,
          onPageChange: setPage,
        }}
      />{' '}
    </div>
  );
}
